/**
 * Module dependencies.
 */

import { Accordion } from '../accordion/accordion';
import { JSX, useEffect, useMemo, useRef, useState } from 'react';
import { OpenCategoriesButton, Wrapper } from './other-category-models.style';
import { ProductItem } from './product-item';
import { snakeCase } from 'lodash';
import { useTranslation } from 'next-i18next';

/**
 * `Props` type.
 */

type Props = {
  catalogCategories: any;
  productId?: string;
};

/**
 * `useClickOutside` hook.
 */

function useClickOutside(ref, onClickOutside) {
  useEffect(() => {
    function handleClick(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        onClickOutside?.(event);
      }
    }

    document.addEventListener('mousedown', handleClick);

    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [ref, onClickOutside]);
}

/**
 * Export `OtherCategoryModels` component.
 */

export function OtherCategoryModels(props: Props): JSX.Element {
  const { catalogCategories } = props;
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const result = useMemo(() => {
    return (catalogCategories ?? [])
      .map((item) => {
        const subCategories = (item.subCategories ?? [])
          .map((subItem) => {
            const products = (subItem.products ?? []).map((product) => {
              let link = '/catalog';

              link = `${link}/${item.id}-${snakeCase(item.title[lang])}`;
              link = `${link}/${subItem.id}-${snakeCase(subItem.title[lang])}`;
              link = `${link}/${product.id}-${snakeCase(product.name[lang])}`;

              return {
                link,
                name: product.name?.[lang] ?? '',
                thumb: product.thumb ?? product.image,
              };
            });

            if (products.length === 0) {
              return null;
            }

            return {
              id: `cat-${item.id}-${subItem.id}`,
              items: products,
              name: subItem.title?.[lang] ?? '',
            };
          })
          .filter(Boolean);

        if (subCategories.length === 0) {
          return null;
        }

        return {
          id: `cat-${item.id}`,
          items: subCategories,
          name: item.title?.[lang] ?? '',
        };
      })
      .filter(Boolean);
  }, [catalogCategories, lang]);

  const ref = useRef(null);
  const [isOpenMenu, setOpenMenu] = useState(false);

  useClickOutside(ref, () => {
    setOpenMenu(false);
  });

  return (
    <>
      <OpenCategoriesButton
        isOpen={isOpenMenu}
        onClick={() => setOpenMenu(!isOpenMenu)}
      >
        {'Produtos'}
      </OpenCategoriesButton>

      <Wrapper isOpen={isOpenMenu} ref={ref}>
        <div>
          {result.map((item) => (
            <Accordion key={item.id} level={'primary'} name={item.name}>
              {item.items.map((subItem) => (
                <Accordion
                  key={subItem.id}
                  level={'secondary'}
                  name={subItem.name}
                >
                  {subItem.items.map((product) => (
                    <ProductItem
                      key={product.id}
                      onClick={() => setOpenMenu(false)}
                      product={product}
                    />
                  ))}
                </Accordion>
              ))}
            </Accordion>
          ))}
        </div>
      </Wrapper>
    </>
  );
}
